import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ParkingFeeListRow } from '../../../../../core/models/finance/master-fee.model';
import { MapperMasterFeeService } from '../../../../../core/mappers/finance/mapper-master-fee.service';
import { ApiMasterFeeService } from '../../../../../core/services/api/finance/api-master-fee.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalParkingFeeComponent } from '../../../modals/finance/modal-parking-fee/modal-parking-fee.component';

@Component({
  selector: 'app-manage-fee',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalParkingFeeComponent],
  templateUrl: './manage-fee.component.html',
  styleUrl: './manage-fee.component.css',
})
export class ManageFeeComponent implements OnInit {
  private readonly api = inject(ApiMasterFeeService);
  private readonly mapper = inject(MapperMasterFeeService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  isLoading = signal(false);
  searchText = signal('');
  currentPage = signal(1);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(1);
  feeItems = signal<ParkingFeeListRow[]>([]);

  isFeeModalOpen = signal(false);
  selectedFee = signal<ParkingFeeListRow | null>(null);

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  ngOnInit(): void {
    this.loadFeeTable();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadFeeTable();
  }

  onSearch(): void {
    this.currentPage.set(1);
    this.loadFeeTable();
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadFeeTable();
    }
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  openCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    this.selectedFee.set(null);
    this.isFeeModalOpen.set(true);
  }

  openEditFee(item: ParkingFeeListRow): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.selectedFee.set(item);
    this.isFeeModalOpen.set(true);
  }

  closeFeeModal(): void {
    this.isFeeModalOpen.set(false);
    this.selectedFee.set(null);
  }

  onFeeSaved(): void {
    this.closeFeeModal();
    this.loadFeeTable();
  }

  formatDate(value?: string | null): string {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('th-TH');
  }

  private loadFeeTable(): void {
    this.isLoading.set(true);
    this.api.getParkingFeeForTable(this.currentPage(), this.pageSize(), this.searchText()).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapFeeTable(res);
        this.feeItems.set(mapped.items);
        this.totalItems.set(mapped.total);
        this.totalPages.set(mapped.totalPages || Math.max(1, Math.ceil(mapped.total / this.pageSize())));
        this.isLoading.set(false);
      },
      error: () => {
        this.feeItems.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดข้อมูลล้มเหลว');
      },
    });
  }
}
